import { useState, useMemo } from 'react';
import { useWorkOrders } from '../../context/WorkOrderContext';
import { useToast } from '../../context/ToastContext';
import WorkOrderCard from './WorkOrderCard';
import WorkOrderDetailPanel from './WorkOrderDetailPanel';
import WMIcon from '../ui/WMIcon';
import EmptyState from '../ui/EmptyState';

const COLUMNS = [
  { key: 'pending',     label: 'Pending',     color: 'text-amber-600 dark:text-amber-400',  dot: 'bg-amber-400' },
  { key: 'in_progress', label: 'In Progress',  color: 'text-blue-600 dark:text-blue-400',    dot: 'bg-blue-400' },
  { key: 'done',        label: 'Done',         color: 'text-green-600 dark:text-green-400',  dot: 'bg-green-400' },
  { key: 'on_hold',     label: 'On Hold',      color: 'text-gray-500 dark:text-gray-400',    dot: 'bg-gray-400' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STEP_DEFAULTS = [
  'Surface prep',
  'Panel alignment',
  'Film application',
  'Heat gun finish',
  'Final inspection',
];

function NewWorkOrderModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    vehicleLabel: '',
    vehicleVin: '',
    assignedTo: '',
    notes: '',
    scheduledStart: '',
    status: 'pending',
  });

  function handleSubmit(e) {
    e.preventDefault();
    const techs = form.assignedTo
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    const steps = STEP_DEFAULTS.map((title, i) => ({
      id: `s${i + 1}`,
      title,
      done: false,
      completedAt: null,
      note: '',
    }));
    onSave({
      ...form,
      assignedTo: techs,
      steps,
      scheduledStart: form.scheduledStart || null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-[#243348]">
          <h2 className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">New Work Order</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#243348] text-[#64748B] dark:text-[#7D93AE] transition-colors">
            <WMIcon name="x-mark" className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] block mb-1">Customer Name *</label>
              <input
                required
                type="text"
                value={form.customerName}
                onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))}
                placeholder="John Smith"
                className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE]"
              />
            </div>
            <div>
              <label className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] block mb-1">Phone</label>
              <input
                type="tel"
                value={form.customerPhone}
                onChange={e => setForm(p => ({ ...p, customerPhone: e.target.value }))}
                placeholder="(555) 000-0000"
                className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE]"
              />
            </div>
            <div>
              <label className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] block mb-1">Status</label>
              <select
                value={form.status}
                onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE]"
              >
                {STATUS_OPTIONS.filter(o => o.value).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] block mb-1">Vehicle *</label>
              <input
                required
                type="text"
                value={form.vehicleLabel}
                onChange={e => setForm(p => ({ ...p, vehicleLabel: e.target.value }))}
                placeholder="2024 Tesla Model Y"
                className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE]"
              />
            </div>
            <div>
              <label className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] block mb-1">VIN</label>
              <input
                type="text"
                value={form.vehicleVin}
                onChange={e => setForm(p => ({ ...p, vehicleVin: e.target.value }))}
                placeholder="17 chars"
                className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE]"
              />
            </div>
            <div>
              <label className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] block mb-1">Scheduled Start</label>
              <input
                type="date"
                value={form.scheduledStart}
                onChange={e => setForm(p => ({ ...p, scheduledStart: e.target.value ? `${e.target.value}T08:00:00Z` : '' }))}
                className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE]"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] block mb-1">Assigned Techs (comma-separated)</label>
              <input
                type="text"
                value={form.assignedTo}
                onChange={e => setForm(p => ({ ...p, assignedTo: e.target.value }))}
                placeholder="Alex R., Jamie K."
                className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE]"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] block mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                rows={2}
                placeholder="Work order notes…"
                className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] resize-none"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-xl border border-gray-200 dark:border-[#243348] text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-2 rounded-xl bg-[#2E8BF0] text-white text-[var(--wm-text-ui)] font-semibold hover:bg-blue-600 transition-colors"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function WorkOrdersPage() {
  const { workOrders, addWorkOrder } = useWorkOrders();
  const { addToast } = useToast();
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [selectedWO, setSelectedWO] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Derive unique techs for filter
  const allTechs = useMemo(() => {
    const set = new Set();
    workOrders.forEach(wo => wo.assignedTo?.forEach(t => set.add(t)));
    return ['', ...Array.from(set).sort()];
  }, [workOrders]);

  // Filtered work orders
  const filtered = useMemo(() => {
    return workOrders.filter(wo => {
      if (filterStatus && wo.status !== filterStatus) return false;
      if (filterAssignee && !wo.assignedTo?.includes(filterAssignee)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !wo.customerName?.toLowerCase().includes(q) &&
          !wo.vehicleLabel?.toLowerCase().includes(q) &&
          !wo.id?.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [workOrders, filterStatus, filterAssignee, searchQuery]);

  function handleNewWorkOrder(data) {
    addWorkOrder(data);
    addToast('Work order updated.', 'success');
    setShowNewModal(false);
  }

  // Keep selectedWO in sync when workOrders update
  const liveSelectedWO = selectedWO
    ? workOrders.find(wo => wo.id === selectedWO.id) || selectedWO
    : null;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-gray-50 dark:bg-[#0F1923]">
      {/* Toolbar */}
      <div className="h-10 bg-white dark:bg-[#1B2A3E] border-b border-gray-200 dark:border-[#243348] flex items-center px-4 gap-3 flex-shrink-0">
        <span className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Work Orders</span>
        <span className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">{filtered.length} orders</span>
        <div className="flex-1" />

        {/* Search */}
        <div className="relative hidden sm:block">
          <WMIcon name="magnifying-glass" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] dark:text-[#7D93AE]" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search…"
            className="pl-8 pr-3 py-1 text-[var(--wm-text-ui)] border border-gray-200 dark:border-[#243348] rounded-lg bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] w-36"
          />
        </div>

        {/* Status filter */}
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="text-[var(--wm-text-ui)] border border-gray-200 dark:border-[#243348] rounded-lg px-2 py-1 bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none"
        >
          {STATUS_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* Assignee filter */}
        <select
          value={filterAssignee}
          onChange={e => setFilterAssignee(e.target.value)}
          className="text-[var(--wm-text-ui)] border border-gray-200 dark:border-[#243348] rounded-lg px-2 py-1 bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none"
        >
          <option value="">All Techs</option>
          {allTechs.filter(Boolean).map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        {/* View toggle */}
        <div className="flex items-center gap-1 border border-gray-200 dark:border-[#243348] rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('kanban')}
            className={`p-1.5 rounded transition-colors ${viewMode === 'kanban' ? 'bg-[#2E8BF0] text-white' : 'text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-100 dark:hover:bg-[#243348]'}`}
            title="Kanban view"
          >
            <WMIcon name="squares-2x2" className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-[#2E8BF0] text-white' : 'text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-100 dark:hover:bg-[#243348]'}`}
            title="List view"
          >
            <WMIcon name="clipboard" className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* New WO button */}
        <button
          onClick={() => setShowNewModal(true)}
          className="wm-btn-primary"
        >
          <WMIcon name="plus" className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">New Work Order</span>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {viewMode === 'kanban' ? (
          <KanbanView
            filtered={filtered}
            onCardClick={setSelectedWO}
          />
        ) : (
          <ListView
            filtered={filtered}
            onCardClick={setSelectedWO}
          />
        )}
      </div>

      {/* Detail panel */}
      {liveSelectedWO && (
        <WorkOrderDetailPanel
          workOrder={liveSelectedWO}
          onClose={() => setSelectedWO(null)}
        />
      )}

      {/* New WO modal */}
      {showNewModal && (
        <NewWorkOrderModal
          onClose={() => setShowNewModal(false)}
          onSave={handleNewWorkOrder}
        />
      )}
    </div>
  );
}

// ─── Kanban View ───────────────────────────────────────────────────────────────
function KanbanView({ filtered, onCardClick }) {
  return (
    <div className="flex gap-4 h-full items-start">
      {COLUMNS.map(col => {
        const cards = filtered.filter(wo => wo.status === col.key);
        return (
          <div key={col.key} className="flex-1 min-w-[220px] max-w-[320px]">
            {/* Column header */}
            <div className="flex items-center gap-2 mb-3 px-1">
              <span className={`w-2 h-2 rounded-full ${col.dot}`} />
              <span className={`text-[var(--wm-text-ui)] font-semibold uppercase tracking-wide ${col.color}`}>
                {col.label}
              </span>
              <span className="ml-auto text-[var(--wm-text-caption)] font-semibold text-[#64748B] dark:text-[#7D93AE] bg-gray-100 dark:bg-[#243348] px-1.5 py-0.5 rounded-full">
                {cards.length}
              </span>
            </div>
            {/* Cards */}
            <div className="space-y-2">
              {cards.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 dark:border-[#243348]">
                  <EmptyState
                    icon="workorders"
                    heading="No work orders yet"
                    body="Work orders are created from approved estimates or added manually."
                    compact
                  />
                </div>
              ) : (
                cards.map(wo => (
                  <WorkOrderCard key={wo.id} workOrder={wo} onClick={onCardClick} />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── List View ────────────────────────────────────────────────────────────────
const STATUS_BADGES = {
  pending:     'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  in_progress: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  done:        'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  on_hold:     'bg-gray-100 dark:bg-gray-700/40 text-gray-600 dark:text-gray-300',
  cancelled:   'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
};

const STATUS_LABELS = {
  pending: 'Pending', in_progress: 'In Progress', done: 'Done', on_hold: 'On Hold', cancelled: 'Cancelled',
};

function ListView({ filtered, onCardClick }) {
  return (
    <div className="bg-white dark:bg-[#1B2A3E] rounded-xl border border-gray-200 dark:border-[#243348] overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-[1fr_1fr_auto_auto_auto] gap-4 px-4 py-2.5 border-b border-gray-200 dark:border-[#243348] text-[var(--wm-text-caption)] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#7D93AE]">
        <span>Vehicle / Customer</span>
        <span>Assigned To</span>
        <span>Progress</span>
        <span>Scheduled</span>
        <span>Status</span>
      </div>
      {filtered.length === 0 ? (
        <EmptyState
          icon="search"
          heading="No work orders match"
          body="Try clearing your filters."
          compact
        />
      ) : (
        filtered.map(wo => {
          const doneCount = wo.steps.filter(s => s.done).length;
          const pct = wo.steps.length > 0 ? Math.round((doneCount / wo.steps.length) * 100) : 0;
          const dateLabel = wo.scheduledStart
            ? new Date(wo.scheduledStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '—';
          return (
            <button
              key={wo.id}
              onClick={() => onCardClick(wo)}
              className="w-full grid grid-cols-[1fr_1fr_auto_auto_auto] gap-4 items-center px-4 py-3 border-b border-gray-100 dark:border-[#243348] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors text-left last:border-0"
            >
              {/* Vehicle / Customer */}
              <div className="min-w-0">
                <p className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate">{wo.vehicleLabel}</p>
                <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] truncate">{wo.customerName}</p>
              </div>
              {/* Assigned */}
              <div className="text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] truncate">
                {wo.assignedTo?.length > 0 ? wo.assignedTo.join(', ') : <span className="text-[#64748B] dark:text-[#7D93AE] italic">Unassigned</span>}
              </div>
              {/* Progress */}
              <div className="flex items-center gap-2 w-24">
                <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-[#243348] overflow-hidden">
                  <div className="h-full rounded-full bg-[#2E8BF0]" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] w-8 text-right">{doneCount}/{wo.steps.length}</span>
              </div>
              {/* Date */}
              <span className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] w-16 text-center">{dateLabel}</span>
              {/* Status */}
              <span className={`text-[var(--wm-text-caption)] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGES[wo.status] || STATUS_BADGES.pending}`}>
                {STATUS_LABELS[wo.status] || wo.status}
              </span>
            </button>
          );
        })
      )}
    </div>
  );
}
